/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from 'react';
import { StyledRegionCardWrapper } from './RegionCardsWrapper.styles';
import { Heading, Messages, Types, useCheckBreakpoint } from '@marriott/mi-ui-library';
import clsx from 'clsx';
import { RegionCardSkeletonLoader } from '../../molecules/RegionCard/RegionCardSkeletonLoader';
import { RegionCard } from '../../molecules';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { useApolloClient } from '@apollo/client';
import { convertToDTTResponse, customSort, extractMarkerData, getRegionDataToStore } from './RegionCardsWrapperHelper';
import {
  RegionCardProps,
  DynamicHotelListingEditableProps,
  RegionCardsWrapperProps,
  UXLPayload,
} from './RegionCardsWrapper.types';
import { RegionMockUXL } from './__mock__/hotelRegionMockUXL';
import {
  phoenixShopSearchPropertiesByLocationInput,
  SearchPropertiesByDestinationForBrands,
} from '@marriott/mi-brands-graphql';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { SearchFacetsList } from '../../constants';

const RegionCardsWrapper = (props: RegionCardsWrapperProps) => {
  const {
    model,
    isAuthorMode = false,
    viewAllHotelsLabel,
    hotelsLabel,
    unitedStatesLabel,
    australiaPILabel,
    mapRegion,
    subRegion,
    brandTag,
    brandSlug,
    regionHeading,
    chinaDisplayLabel,
    hotelDirectoryTemporarilyUnavailableMsg,
    componentId,
  } = props;
  // Parse props
  const regionName = mapRegion ? JSON.parse(mapRegion) : [];
  const subRegionName = subRegion ? JSON.parse(subRegion) : {};
  const labelForChina = chinaDisplayLabel;
  const requestId = useClientEnvVarsStore.getState().envVarsObject['REQUEST_ID'];
  const currentLocale = useClientEnvVarsStore.getState().envVarsObject['ACCEPT_LANGUAGE'];
  const isDTT = useClientEnvVarsStore.getState().envVarsObject['IS_DTT'];
  // State: UI and Data
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [hasUXLEndpointError, setHasUXLEndpointError] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState<RegionCardProps | null>(null);
  const [regionList, setRegionList] = useState<RegionCardProps[]>([]);
  const [subRegionList, setSubRegionList] = useState<any[]>([]);
  const [dataResponseFromUXLEndpoint, setDataResponseFromUXLEndpoint] = useState<any>(null);
  const isViewportM = useCheckBreakpoint('viewportM'); // Responsive UI helper
  const client = useApolloClient(); // Apollo GraphQL client

  /**
   * Fetch region data from UXL or use mock data in author mode
   */
  const fetchRegionData = async (inputValues: any) => {
    if (isAuthorMode) {
      return RegionMockUXL;
    }
    const { data } = await client.query({
      query: isDTT ? phoenixShopSearchPropertiesByLocationInput : SearchPropertiesByDestinationForBrands,
      ...inputValues,
    });
    return isDTT ? data : convertToDTTResponse(data);
  };
  /**
   * Fetch and transform region data for UI rendering
   */
  const getRegionCountFromUXL = async (inputValues: any) => {
    try {
      const data = await fetchRegionData(inputValues);
      setDataResponseFromUXLEndpoint(data); // Raw UXL data
      setIsDataLoaded(true); // Mark loading done

      // Transform data to usable region objects
      const regionDataToStore = getRegionDataToStore(
        regionName,
        subRegionName,
        data,
        model.mexicoLabel,
        model.canadaLabel
      ).sort(customSort(currentLocale?.replace('_', '-')));

      // edge case: when a location where no result present
      if (regionDataToStore.length === 0 || regionDataToStore.every(reg => reg.count === 0)) {
        setHasUXLEndpointError(true);
      } else {
        setRegionList(regionDataToStore); // Store regions for rendering
        setSelectedRegion(regionDataToStore[0] ?? null); // Default to first region
      }
    } catch (error) {
      setHasUXLEndpointError(true);
    }
  };

  /**
   * Effect: Initial fetch on mount
   */
  useEffect(() => {
    const inputValues: UXLPayload = {
      options: { countsOnly: true },
      facets: {
        terms: [
          {
            type: SearchFacetsList?.brands,
            dimensions: [(brandTag || brandSlug)?.toUpperCase()],
          },
          {
            type: SearchFacetsList?.state,
            dimensions: [],
          },
          {
            type: SearchFacetsList?.country,
            dimensions: [],
          },
          {
            type: SearchFacetsList?.leisureRegion,
            dimensions: [],
          },
        ],
      },
    };

    if (!isDTT) {
      inputValues.destination = '';
    }

    getRegionCountFromUXL({
      variables: { search: inputValues },
      context: {
        headers: {
          'x-request-id': requestId,
          'accept-language': currentLocale?.replace('_', '-'),
        },
      },
    });
  }, []);

  /**
   * Effect: Update sub-region list when selected region changes
   */
  useEffect(() => {
    if (!selectedRegion || !dataResponseFromUXLEndpoint) return;

    const subRegionData = extractMarkerData(
      dataResponseFromUXLEndpoint,
      selectedRegion,
      subRegionName?.subRegion,
      labelForChina,
      model.australiaPILabel,
      model.mexicoLabel,
      model.canadaLabel,
      model.australiaAndOceaniaLabel
    );

    setSubRegionList(subRegionData);
  }, [selectedRegion, dataResponseFromUXLEndpoint]);

  /**
   * Computed: Determine if we should show loading state
   */
  const isLoading = !isDataLoaded;
  /**
   * Helper: Render all RegionCards once data is ready
   */
  const renderRegionCards = () => (
    <div id={`region-card-${componentId}`} className={clsx('col-12', 'p-0', isViewportM ? '' : 'px-3')}>
      {regionHeading && (
        <div className="my-4">
          <Heading
            variation={Types.headingType.title}
            titleText={regionHeading}
            fontSize={Types.size.medium}
            element={Types.tags.h3}
            customClass="text-center"
          />
        </div>
      )}
      {regionList.map((region, index) => {
        if (!region || region.count <= 0 || !region.code) return null;

        return (
          <RegionCard
            key={`${region.code || 'region'}-${index}`}
            region={region}
            name={region.label ?? ''}
            code={region?.code}
            count={region?.count}
            customLabel={region?.customLabel ?? ''}
            viewAllHotelsLabel={viewAllHotelsLabel}
            hotelsLabel={hotelsLabel}
            unitedStatesLabel={unitedStatesLabel}
            australiaPILabel={australiaPILabel}
            subRegionList={subRegionList}
            setSelectedRegion={setSelectedRegion}
            selectedRegion={selectedRegion}
            currentLocale={currentLocale}
            brandCode={brandTag || brandSlug}
          />
        );
      })}
    </div>
  );

  /**
   * Render region cards or loading state
   */
  return (
    <StyledRegionCardWrapper>
      <div className={clsx('region-card-wrapper')}>
        {hasUXLEndpointError && hotelDirectoryTemporarilyUnavailableMsg && (
          <div className={clsx('text-left')}>
            <Messages messageType={'info'} messageHeading={hotelDirectoryTemporarilyUnavailableMsg} />
          </div>
        )}
        {isLoading ? (
          <>
            <RegionCardSkeletonLoader key="skeleton-header" type="header-only" />
            {[...Array(3)].map((_, index) => (
              <RegionCardSkeletonLoader key={`skeleton-${index}`} type="" />
            ))}
          </>
        ) : (
          <div className={clsx('row', '')}>{regionList.length > 0 && renderRegionCards()}</div>
        )}
      </div>
    </StyledRegionCardWrapper>
  );
};

/**
 * AEM Editable wrapper for RegionCardsWrapper
 */
const RegionCardsWrapperConfig = {
  emptyLabel: 'RegionCardsWrapper',
  isEmpty: () => false,
  resourceType: 'mi-aem-brands-spa/components/content/dynamichotellisting/v1/dynamichotellisting',
};

export const DynamicHotelListingEditable = (props: DynamicHotelListingEditableProps) => (
  <EditableComponent config={RegionCardsWrapperConfig} {...props}>
    <RegionCardsWrapper {...props} />
  </EditableComponent>
);
export { RegionCardsWrapper };
